"use client";

import { isEmpty } from "lodash";
import { useSearchParams } from "next/navigation";
import {
  ChangeEvent,
  FunctionComponent,
  ReactElement,
  useEffect,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import useRedirect from "rivals/components/pages/Registration/useRedirect";
import Button from "rivals/components/shared/Button";
import RivalsLink from "rivals/components/shared/Link";
import { SubscriptionPlanSelection } from "rivals/components/shared/Subscription/SubscriptionPlan";
import Splatter from "rivals/components/shared/Svgs/Backgrounds/Splatter";
import GoBigGetPremium from "rivals/components/shared/Svgs/GoBigGetPremium";
import RivalsLogomarkBlock from "rivals/components/shared/Svgs/Logos/RivalsLogomarkBlock";
import { getPublisherLetter as getPublisherLetterAction } from "rivals/redux/registration/actions";
import {
  getPublisherLetter,
  getSubmitting
} from "rivals/redux/registration/selectors";
import { PATHS, PLANS, PurchasablePlan } from "rivals/shared/constants";
import { Page } from "rivals/shared/interfaces/Promotion";
import { siteAccentColor } from "rivals/shared/utils/colors";
import { setData } from "rivals/shared/utils/qa";
import FAQSection from "./FAQSection";
import PublisherLetter from "./PublisherLetter";
import styles from "./SignUp.module.scss";
import strings from "./strings";
import { DispatchProps, StateProps } from "./types";

export type Props = DispatchProps & StateProps;

const SignUp: FunctionComponent<Props> = ({
  getActivePromotion,
  isAuthenticated,
  isSubscribed,
  promotion,
  railsConstants,
  selectedSite,
  setIsRedirecting,
  user
}) => {
  const {
    alreadyHaveAccount,
    annualBullets,
    annualCostDescription,
    joinForFree,
    logIn,
    monthlyBullets,
    monthlyCostDescription,
    toSite,
    signUp,
    subscribe,
    whySubscribe
  } = strings;

  const [selectedPlan, selectPlan] = useState<PurchasablePlan>(PLANS.ANNUAL);
  const searchParams = useSearchParams();
  const promoCode = searchParams?.get("promo_code");
  const referralCode = searchParams?.get("ref");
  const ncid = searchParams?.get("ncid");
  const submitting = useSelector(getSubmitting);
  const publisherLetter = useSelector(getPublisherLetter);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!submitting && publisherLetter === undefined && selectedSite) {
      dispatch(getPublisherLetterAction(selectedSite.id));
    }
  }, [submitting, publisherLetter, dispatch, selectedSite]);

  useEffect(() => {
    if (promoCode) {
      getActivePromotion(promoCode as string);
    }
  }, [getActivePromotion, promoCode]);

  useEffect(() => {
    if (promotion) {
      const { selectedPlan: plan } = promotion;
      if (plan === PLANS.ANNUAL || plan === PLANS.MONTHLY) {
        selectPlan(plan);
      }
    }
  }, [promotion]);

  const referralUrlParams = {
    promoCode: promoCode as string,
    ref: referralCode as string
  };

  useRedirect({
    isAuthenticated,
    isSubscribed,
    referralUrlParams,
    selectedSite,
    setIsRedirecting,
    submitClicked: false,
    user
  });

  if (!railsConstants || !selectedSite) {
    return null;
  }

  const color = siteAccentColor(selectedSite);

  function promoSection({ title, subTitle }: Page): ReactElement {
    return (
      <div
        className={styles.promoSection}
        style={
          selectedSite
            ? { backgroundColor: `#${selectedSite.primaryColor}` }
            : {}
        }
      >
        <div>{title}</div>
        <span>{subTitle}</span>
      </div>
    );
  }

  function subscribeUrl(): string {
    let url = `${PATHS.SUBSCRIBE}?plan=${selectedPlan}`;
    if (promotion) {
      url = `${url}&promo_code=${promoCode}`;
    }
    if (referralCode) {
      url = `${url}&ref=${referralCode}`;
    }
    if (ncid) {
      url = `${url}&ncid=${ncid}`;
    }
    return url;
  }

  function subscribeButton(invert?: boolean): ReactElement {
    return (
      <Button
        buttonText={subscribe}
        data="sign-up-plan-select-submit"
        disabled={!selectedPlan}
        inverted={invert}
        url={subscribeUrl()}
      />
    );
  }

  return (
    <div className={styles.signUp}>
      {promotion && promotion.page && promoSection(promotion.page)}
      <div className={styles.headerSection} {...setData("sign-up-header")}>
        <div className={styles.splatter}>
          <Splatter fill={color} />
        </div>
        <div className={styles.headerTitle}>
          <div className={styles.logo}>
            <RivalsLogomarkBlock fill={color} philter={"sign-up"} />
          </div>
          <div className={styles.title}>
            <GoBigGetPremium />
          </div>
        </div>
        <div
          className={styles.headerSubtitle}
          {...setData("sign-up-header-subtitle")}
        >
          {strings.subtitle}
        </div>
      </div>
      <div className={styles.planSection}>
        <div className={styles.header}>
          <span>{whySubscribe}</span>
          <span>{toSite(selectedSite.name)}</span>
        </div>
        <SubscriptionPlanSelection
          action={subscribeButton(true)}
          className={styles.planSelection}
          onChange={(e?: ChangeEvent<HTMLInputElement>): void => {
            e && selectPlan(e.target.value as PurchasablePlan);
          }}
          plans={[
            {
              bullets: annualBullets(selectedSite.name),
              cost: annualCostDescription(railsConstants.annualCostDollars),
              planType: PLANS.ANNUAL,
              savings: railsConstants.monthlySavings
            },
            {
              bullets: monthlyBullets(selectedSite.name),
              cost: monthlyCostDescription(railsConstants.monthlyCostDollars),
              planType: PLANS.MONTHLY
            }
          ]}
          promotion={promotion}
          selectedPlan={selectedPlan}
          withBenefits
        />
        <div className={styles.registrationLink}>
          {joinForFree}
          <RivalsLink data="sign-up-free-link" href={PATHS.CREATE_ACCOUNT}>
            {signUp}
          </RivalsLink>
        </div>
        <div className={styles.registrationLink}>
          {alreadyHaveAccount}
          <RivalsLink data="sign-up-log-in-link" href={PATHS.SIGN_IN}>
            {logIn}
          </RivalsLink>
        </div>
      </div>
      {publisherLetter && !isEmpty(publisherLetter) && (
        <PublisherLetter
          letter={publisherLetter}
          siteName={selectedSite.friendlyName}
        />
      )}
      <FAQSection selectedSiteName={selectedSite.name} />
    </div>
  );
};

export default SignUp;
